import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import React from 'react';
import Card, { type CardNames } from '../../../components/Card/Card.tsx';
import type { CancelRefundSummary } from '../../../lib/types.ts';
import AppDownloadBanner from '../AppDownloadBanner/AppDownloadBanner.tsx';
import Header from '../Header/Header.tsx';
import { CanceledAtom } from '../lib/state.ts';

import styles from './Canceled.module.css';

const CARD_LABELS: Record<string, string> = {
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  mastercard: 'Mastercard',
  visa: 'Visa',
};

function cardLabel(summary: CancelRefundSummary): string {
  const brand = summary.card_brand ? (CARD_LABELS[summary.card_brand] ?? 'Card') : 'Card';
  return summary.card_last_four
    ? `${brand} •••• ${summary.card_last_four}`
    : `Your ${brand.toLowerCase()}`;
}

function hasAnythingBack(summary: CancelRefundSummary): boolean {
  return summary.card_refund > 0 || summary.credits > 0 || summary.fun_coupons > 0;
}

/** Line-by-line list of what the cancellation returned: card, credits, Fun Coupons. */
const RefundSummary: React.FC<{ summary: CancelRefundSummary }> = ({ summary }) => (
  <section className={styles.summary} aria-label="What you're getting back">
    <span className={styles.eyebrow}>What you&rsquo;re getting back</span>

    <ul className={styles.rows}>
      {summary.card_refund > 0 && (
        <li className={styles.row}>
          <Card name={(summary.card_brand ?? '') as CardNames} className={styles.cardIcon} />
          <div className={styles.rowText}>
            <span className={styles.rowLabel}>{cardLabel(summary)}</span>
            <span className={styles.rowCaption}>
              Refunded to your card &middot; 5&ndash;10 business days
            </span>
          </div>
          <span className={styles.amount}>{summary.card_refund__currency}</span>
        </li>
      )}
      {summary.credits > 0 && (
        <li className={styles.row}>
          <span className={styles.walletIcon} aria-hidden="true" />
          <div className={styles.rowText}>
            <span className={styles.rowLabel}>Booking credits</span>
            <span className={styles.rowCaption}>In your Dorsia wallet &middot; never expire</span>
          </div>
          <span className={styles.amount}>{summary.credits__currency}</span>
        </li>
      )}
      {summary.fun_coupons > 0 && (
        <li className={styles.row}>
          <span className={styles.walletIcon} aria-hidden="true" />
          <div className={styles.rowText}>
            <span className={styles.rowLabel}>Fun Coupons</span>
            <span className={styles.rowCaption}>In your Dorsia wallet</span>
          </div>
          <span className={styles.amount}>{summary.fun_coupons__currency}</span>
        </li>
      )}
    </ul>
  </section>
);

const Canceled: React.FC = () => {
  const cancellation = useAtomValue(CanceledAtom);

  if (!cancellation) {
    return <Header title="Your reservation has already been canceled." />;
  }

  const summary = cancellation.refund_summary;
  const showSummary = !!summary && hasAnythingBack(summary);
  const isGift = cancellation.choice === 'gift';
  // Point people to the app whenever something landed in their wallet.
  const hasWalletValue = !!summary && (summary.credits > 0 || summary.fun_coupons > 0);

  return (
    <>
      <Header title="Your reservation has been canceled." />

      {(isGift || showSummary) && (
        <div className={clsx('content', styles.content)}>
          {isGift && (
            <p className={styles.text}>
              Your booking credits are in your Dorsia wallet, and your profile is ready to claim 30
              days of complimentary access.
            </p>
          )}

          {showSummary && <RefundSummary summary={summary} />}

          {(isGift || hasWalletValue) && (
            <AppDownloadBanner
              label={isGift ? 'Next step' : 'Dorsia app'}
              text={
                isGift
                  ? 'Download Dorsia to claim your access and use your credits'
                  : 'Download Dorsia to use what’s in your wallet'
              }
            />
          )}
        </div>
      )}
    </>
  );
};

export default Canceled;
